import React, { useState, useEffect } from 'react';
import axios from 'axios';
import '../css/Users.css'; // Import the new CSS file
import { faFileCsv, faFileExcel } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

const Users = () => {
    const [users, setUsers] = useState([]);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;


    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showAddModal, setShowAddModal] = useState(false);
    const [userToDelete, setUserToDelete] = useState(null);
    const [userToEdit, setUserToEdit] = useState(null);

    const [newUserEmail, setNewUserEmail] = useState('');
    const [newUserPassword, setNewUserPassword] = useState('');
    const [newUserRole, setNewUserRole] = useState('user');
    const [editUserEmail, setEditUserEmail] = useState('');
    const [editUserRole, setEditUserRole] = useState('');

    useEffect(() => {
        fetchUsers();
    }, []);

    const fetchUsers = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${API_BASE_URL}/admin/users`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setUsers(response.data);
            setLoading(false);
        } catch (error) {
            setError(error.response?.data?.message || 'Failed to fetch users');
            setLoading(false);
        }
    };

    // Add User
    const handleAddUser = async () => {
        try {
            const token = localStorage.getItem('token');
            await axios.post(`${API_BASE_URL}/admin/users`, {
                email: newUserEmail,
                password: newUserPassword,
                role: newUserRole,
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setSuccessMessage('User added successfully');
            setNewUserEmail('');
            setNewUserPassword('');
            setNewUserRole('user');
            setShowAddModal(false);
            fetchUsers();
        } catch (error) {
            setError(error.response?.data?.message || 'Failed to add user');
        }
    };

    // Edit User
    const handleEditUser = async () => {
        if (!userToEdit) return;
        try {
            const token = localStorage.getItem('token');
            await axios.put(`${API_BASE_URL}/admin/users/${userToEdit.id}`, {
                email: editUserEmail,
                role: editUserRole,
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setSuccessMessage('User updated successfully');
            setEditUserEmail('');
            setEditUserRole('');
            setShowEditModal(false);
            fetchUsers();
        } catch (error) {
            setError(error.response?.data?.message || 'Failed to update user');
        }
    };

    // Delete User
    const handleDeleteUser = async () => {
        if (!userToDelete) return;
        try {
            const token = localStorage.getItem('token');
            await axios.delete(`${API_BASE_URL}/admin/users/${userToDelete.id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setSuccessMessage('User deleted successfully');
            setShowDeleteModal(false);
            fetchUsers();
        } catch (error) {
            setError(error.response?.data?.message || 'Failed to delete user');
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return isNaN(date) ? 'Invalid Date' : date.toLocaleString();
    };

    const downloadCSV = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${API_BASE_URL}/admin/users/csv`, {
                headers: { Authorization: `Bearer ${token}` },
                responseType: 'blob',
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'users.csv');
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (error) {
            setError('Error downloading CSV');
        }
    };

    const downloadExcel = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${API_BASE_URL}/admin/users/excel`, {
                headers: { Authorization: `Bearer ${token}` },
                responseType: 'blob',
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'users.xlsx');
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (error) {
            setError('Error downloading Excel');
        }
    };

    return (
        <div className="users-container">
            <h2>User Management</h2>
            {error && <p className="error-message">{error}</p>}
            {successMessage && <p className="success-message">{successMessage}</p>}
            <button className="add-user-btn" onClick={() => setShowAddModal(true)}>
                Add User
            </button>
            <div className="export-buttons">
                <button onClick={downloadCSV}>
                    <FontAwesomeIcon icon={faFileCsv} /> Download CSV
                </button>
                <button onClick={downloadExcel}>
                    <FontAwesomeIcon icon={faFileExcel} /> Download Excel
                </button>
            </div>
            {loading ? (
                <p>Loading users...</p>
            ) : (
                <div className="user-cards">
                    {users.map((user) => (
                        <div className="user-card" key={user.id}>
                            <div className="card-header">
                                <h3>{user.email}</h3>
                                <span className={`role ${user.role}`}>{user.role}</span>
                            </div>
                            <div className="card-body">
                                <p>Registered: {formatDate(user.registration_time)}</p>
                                <p>Last Seen: {formatDate(user.last_seen)}</p>
                                <p>Online Duration: {user.online_duration} seconds</p>
                            </div>
                            <div className="card-footer">
                                <button className="edit-btn" onClick={() => {
                                    setUserToEdit(user);
                                    setEditUserEmail(user.email);
                                    setEditUserRole(user.role);
                                    setShowEditModal(true);
                                }}>
                                    Edit
                                </button>
                                <button className="delete-btn" onClick={() => {
                                    setUserToDelete(user);
                                    setShowDeleteModal(true);
                                }}>
                                    Delete
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Add User Modal */}
            {showAddModal && (
                <div className="modal">
                    <div className="modal-content">
                        <span className="close" onClick={() => setShowAddModal(false)}>&times;</span>
                        <h3>Add New User</h3>
                        <input
                            type="email"
                            placeholder="Email"
                            value={newUserEmail}
                            onChange={(e) => setNewUserEmail(e.target.value)}
                        />
                        <input
                            type="password"
                            placeholder="Password"
                            value={newUserPassword}
                            onChange={(e) => setNewUserPassword(e.target.value)}
                        />
                        <select
                            value={newUserRole}
                            onChange={(e) => setNewUserRole(e.target.value)}
                        >
                            <option value="user">User</option>
                            <option value="admin">Admin</option>
                        </select>
                        <button className="save-btn" onClick={handleAddUser}>Add User</button>
                    </div>
                </div>
            )}

            {/* Edit User Modal */}
            {showEditModal && userToEdit && (
                <div className="modal">
                    <div className="modal-content">
                        <span className="close" onClick={() => setShowEditModal(false)}>&times;</span>
                        <h3>Edit User</h3>
                        <input
                            type="email"
                            placeholder="Email"
                            value={editUserEmail}
                            onChange={(e) => setEditUserEmail(e.target.value)}
                        />
                        <select
                            value={editUserRole}
                            onChange={(e) => setEditUserRole(e.target.value)}
                        >
                            <option value="user">User</option>
                            <option value="admin">Admin</option>
                        </select>
                        <button className="save-btn" onClick={handleEditUser}>Save Changes</button>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {showDeleteModal && userToDelete && (
                <div className="modal">
                    <div className="modal-content">
                        <span className="close" onClick={() => setShowDeleteModal(false)}>&times;</span>
                        <h3>Confirm Deletion</h3>
                        <p>Are you sure you want to delete user {userToDelete.email}?</p>
                        <button className="confirm-delete-btn" onClick={handleDeleteUser}>Yes, Delete</button>
                        <button className="cancel-btn" onClick={() => setShowDeleteModal(false)}>Cancel</button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Users;
